import { Controller } from "@hotwired/stimulus";

const COUNT_SLIDE_DURATION_MS = 120;
const BADGE_PULSE_DURATION_MS = 180;
const COUNT_ANIMATION_ID = "sidebar-ticket-badge-count";
const BADGE_ANIMATION_ID = "sidebar-ticket-badge-pulse";

export default class extends Controller<HTMLElement> {
    private count = 0;
    private observer: MutationObserver | null = null;

    connect(): void {
        this.count = this.currentCount();
        this.observer = new MutationObserver(() => this.animateCountChange());
        this.observer.observe(this.element, {
            attributeFilter: ["data-awaiting-action-ticket-count"],
            attributes: true,
            characterData: true,
            childList: true,
            subtree: true,
        });
    }

    disconnect(): void {
        this.observer?.disconnect();
        this.observer = null;
    }

    private animateCountChange(): void {
        const nextCount = this.currentCount();
        if (nextCount === this.count) {
            return;
        }

        const previousCount = this.count;
        this.count = nextCount;

        const badge = this.element.querySelector<HTMLElement>('[data-testid="sidebar-ticket-badge"]');
        const count = this.element.querySelector<HTMLElement>("[data-sidebar-ticket-badge-count]");
        if (
            !badge ||
            !count ||
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            return;
        }

        count.getAnimations()
            .filter((animation) => animation.id === COUNT_ANIMATION_ID)
            .forEach((animation) => animation.cancel());
        badge.getAnimations()
            .filter((animation) => animation.id === BADGE_ANIMATION_ID)
            .forEach((animation) => animation.cancel());

        const slideOffset = nextCount >= previousCount ? 3 : -3;
        const countAnimation = count.animate(
            [
                { opacity: 0.55, transform: `translateY(${slideOffset}px)` },
                { opacity: 1, transform: "translateY(0)" },
            ],
            {
                duration: COUNT_SLIDE_DURATION_MS,
                easing: "cubic-bezier(0.22, 1, 0.36, 1)",
            },
        );
        countAnimation.id = COUNT_ANIMATION_ID;

        const badgeAnimation = badge.animate(
            [
                { transform: "scale(1)" },
                { transform: "scale(1.08)", offset: 0.45 },
                { transform: "scale(1)" },
            ],
            {
                duration: BADGE_PULSE_DURATION_MS,
                easing: "ease-out",
            },
        );
        badgeAnimation.id = BADGE_ANIMATION_ID;

        this.element.dispatchEvent(
            new CustomEvent("sidebar-ticket-badge-motion", {
                bubbles: true,
                detail: {
                    countDuration: COUNT_SLIDE_DURATION_MS,
                    nextCount,
                    previousCount,
                    pulseDuration: BADGE_PULSE_DURATION_MS,
                },
            }),
        );
    }

    private currentCount(): number {
        const count = Number.parseInt(this.element.dataset.awaitingActionTicketCount || "0", 10);
        return Number.isFinite(count) ? count : 0;
    }
}
